import { VisibilityCondition, ComplexVisibilityCondition } from '../types';

/**
 * Evaluates if a field should be visible based on its visibility conditions
 * @param conditions Visibility conditions to evaluate
 * @param formValues Current form values
 * @returns Boolean indicating if the field should be visible
 */
export function evaluateVisibility(
  conditions: VisibilityCondition[] | ComplexVisibilityCondition | undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formValues: Record<string, any>
): boolean {
  // If no conditions, field is always visible
  if (!conditions) {
    return true;
  }

  // Handle complex conditions (AND/OR)
  if ('type' in conditions) {
    if (!conditions.conditions || !Array.isArray(conditions.conditions)) {
      return true;
    }

    if (conditions.type === 'AND') {
      return conditions.conditions.every(condition => 
        evaluateSingleCondition(condition, formValues)
      );
    } else if (conditions.type === 'OR') {
      return conditions.conditions.some(condition => 
        evaluateSingleCondition(condition, formValues)
      );
    }
    
    return true;
  }
  
  // Handle array of conditions (implicit AND)
  if (Array.isArray(conditions)) {
    return conditions.every(condition => 
      evaluateSingleCondition(condition, formValues)
    );
  }
  
  return true;
}

/**
 * Evaluates a single visibility condition
 * @param condition Condition to evaluate
 * @param formValues Current form values
 * @returns Boolean indicating if the condition is met
 */
function evaluateSingleCondition(
  condition: VisibilityCondition,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formValues: Record<string, any>
): boolean {
  const { field, value, operator } = condition;
  const fieldValue = formValues[field];
  
  switch (operator) {
    case 'equals':
      return fieldValue === value;
    case 'notEquals':
      return fieldValue !== value;
    case 'contains':
      if (Array.isArray(fieldValue)) {
        return fieldValue.includes(value);
      }
      if (typeof fieldValue === 'string') {
        return fieldValue.includes(String(value));
      }
      return false;
    case 'notContains':
      if (Array.isArray(fieldValue)) {
        return !fieldValue.includes(value);
      }
      if (typeof fieldValue === 'string') {
        return !fieldValue.includes(String(value));
      }
      return true;
    case 'isEmpty':
      return !fieldValue || fieldValue === '' || fieldValue === null || fieldValue === undefined;
    case 'notEmpty':
      return !!fieldValue && fieldValue !== '';
    default:
      return true;
  }
}
